import { useState, Component } from 'react';
import { Analytics } from '@vercel/analytics/react';
import BootSequence from './v2/v3/BootSequence';
import { Sentry } from './sentry.js';

class ErrorBoundary extends Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(e) { return { err: e }; }
  componentDidCatch(error, info) {
    Sentry.captureException(error, { contexts: { react: { componentStack: info?.componentStack } } });
  }
  render() {
    if (this.state.err) return (
      // Plain literals on purpose: the old var(--surface/--text/--muted2) names
      // were deleted with V1, so the crash screen rendered unstyled. A crash
      // screen must not depend on any stylesheet having loaded.
      <div style={{padding:40,textAlign:"center",color:"#B8B2A6",background:"#000",minHeight:"100vh"}}>
        <div style={{fontSize:32,marginBottom:12}}>!</div>
        <p style={{fontWeight:700,marginBottom:6,color:"#EDE8DC"}}>Something went wrong on this page</p>
        <p style={{fontSize:12,color:"#8A8578",marginBottom:16}}>{this.state.err.message}</p>
        <button onClick={()=>this.setState({err:null})} style={{background:"#14110D",border:"1px solid #2E2A24",borderRadius:6,padding:"6px 14px",color:"#EDE8DC",cursor:"pointer",fontSize:12}}>Retry</button>
      </div>
    );
    return this.props.children;
  }
}
import { SignedIn, SignedOut, SignIn } from '@clerk/clerk-react';
import { useIsMobile } from './hooks.js';
import { V2TodayPage } from './v2/pages/Today.jsx';
import { V2MarketsPage } from './v2/pages/Markets.jsx';
import V2SignalsPage from './v2/pages/Signals.jsx';
import { V2InsightsPage } from './v2/pages/Insights.jsx';
import { V2InsightDetailPage } from './v2/pages/InsightDetail.jsx';
import { Discover as V2DiscoverPage } from './v2/pages/Discover.jsx';
import { V2CalendarPage } from './v2/pages/Calendar.jsx';
import { V2MyStocksPage } from './v2/pages/MyStocks.jsx';
import { V2MorePage } from './v2/pages/More.jsx';
import { V2LeveragePage } from './v2/pages/Leverage.jsx';
import { V2GlobalPage } from './v2/pages/Global.jsx';
import { V2AgentDeskPage } from './v2/pages/AgentDesk.jsx';
import { V2AccountsPage } from './v2/pages/Accounts.jsx';
import { V2CyclePage } from './v2/pages/Cycle.jsx';
import { V2SettingsPage } from './v2/pages/Settings.jsx';
import { useV2Path } from './v2/lib/useV2Path.js';
import { DesktopV3Router } from './v3desktop/DesktopV3Router.jsx';

// Settings → "Open to" (default_tab pref, 'home' | 'watch'). LAUNCH-TIME ONLY:
// runs once at module load, before the routers mount, so in-app navigation to
// '/' (the Overview tab) is never affected. Reads the useUserPref localStorage
// mirror synchronously — signed-in server values land in the mirror on first
// hydrate, so the pref applies from the next launch on a fresh device.
try {
  if (typeof window !== 'undefined' && window.location.pathname === '/'
    && JSON.parse(window.localStorage.getItem('atlas.pref.default_tab')) === 'watch') {
    window.history.replaceState(null, '', '/my-stocks');
  }
} catch { /* private mode / bad JSON — land on Overview as before */ }
import { Admin as V3Admin } from './v3desktop/pages/Admin.jsx';

function V2Router() {
  // Subscribes to pushState (from v2Navigate) + popstate. Re-renders on
  // in-app nav without a full reload.
  const path = useV2Path();
  if (path.startsWith('/v2/signals') || path.startsWith('/signals')) {
    return <V2SignalsPage />;
  }
  if (path.startsWith('/v2/markets') || path.startsWith('/markets')) {
    return <V2MarketsPage />;
  }
  if (path.startsWith('/v2/watchlist') || path.startsWith('/watchlist')) {
    // Redirect to /my-stocks (the combined Portfolio + Watchlist page).
    window.history.replaceState(null, '', '/my-stocks');
    return <V2MyStocksPage />;
  }
  // /insights/:id detail BEFORE the list route — order matters for prefix match.
  if (path.match(/^\/(v2\/)?insights\/[^/]+/)) {
    return <V2InsightDetailPage />;
  }
  if (path.startsWith('/v2/insights') || path.startsWith('/insights')) {
    return <V2InsightsPage />;
  }
  if (path.startsWith('/v2/discover') || path.startsWith('/discover')) {
    return <V2DiscoverPage />;
  }
  if (path.startsWith('/v2/calendar') || path.startsWith('/calendar')) {
    return <V2CalendarPage />;
  }
  if (path.startsWith('/v2/my-stocks') || path.startsWith('/my-stocks')) {
    return <V2MyStocksPage />;
  }
  if (path.startsWith('/v2/my-portfolio') || path.startsWith('/my-portfolio')) {
    // The manual portfolio now lives on the Accounts stop of the cluster.
    window.history.replaceState(null, '', '/accounts');
    return <V2AccountsPage />;
  }
  if (path.startsWith('/v2/accounts') || path.startsWith('/accounts')) {
    return <V2AccountsPage />;
  }
  if (path.startsWith('/v2/more') || path.startsWith('/more')) {
    return <V2MorePage />;
  }
  if (path.startsWith('/v2/leverage') || path.startsWith('/leverage')) {
    return <V2LeveragePage />;
  }
  if (path.startsWith('/v2/global') || path.startsWith('/global')) {
    return <V2GlobalPage />;
  }
  if (path.startsWith('/v2/agent') || path.startsWith('/agent')) {
    return <V2AgentDeskPage />;
  }
  if (path.startsWith('/v2/cycle') || path.startsWith('/cycle')) {
    return <V2CyclePage />;
  }
  if (path.startsWith('/v2/settings') || path.startsWith('/settings')) {
    return <V2SettingsPage />;
  }
  // Legacy /hunting → /discover redirect for any bookmarked links.
  if (path.startsWith('/v2/hunting') || path.startsWith('/hunting')) {
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', '/discover');
    }
    return <V2DiscoverPage />;
  }
  if (path.startsWith('/v2/calendar') || path.startsWith('/calendar')) {
    return <V2CalendarPage />;
  }
  if (path.startsWith('/admin')) {
    // V3-native admin (shell-agnostic page shared with the desktop router).
    return (
      <div className="v3-root" style={{ minHeight: '100vh' }}>
        <div style={{ padding: '24px 16px 80px' }}>
          <V3Admin />
        </div>
      </div>
    );
  }
  return <V2TodayPage />;
}

export default function AIAtlas() {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  // V3 desktop (src/v3desktop/*) is now the DEFAULT desktop experience — a bare
  // stonk.world desktop visit renders it. `/v3` is kept as an explicit alias
  // that forces the V3 desktop layout at ANY width (old bookmarks / phone
  // preview). Internal /v3 page nav is local state, so the path stays put.
  const isV3Force = path === '/v3' || path.startsWith('/v3/');
  // Device split: phones (<768px) get the V3 mobile redesign; desktop defaults
  // to V3 desktop. Both retired museums now fall through to the default V3
  // surfaces: V1 retired 2026-07-12 (last commit d52d506), the V2 desktop
  // museum retired 2026-07-14 (last commit 096168b). `git checkout <sha>`
  // resurrects either. See DECISION_LOG + "Retired product surfaces".
  const isMobile = useIsMobile();

  // Boot intro — the studio `boot-sequence` globe splash over the v3 (mobile)
  // app. Plays once per browser session (sessionStorage gate) on first load,
  // then fades out. Desktop (V2) doesn't get it.
  const [booting, setBooting] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return !window.sessionStorage.getItem('atlas_booted'); } catch { return true; }
  });
  const endBoot = () => {
    try { window.sessionStorage.setItem('atlas_booted', '1'); } catch { /* private mode */ }
    setBooting(false);
  };

  return (
    <>
      <SignedOut>
        {/* Studio palette as plain literals — this screen renders before any
            theme/token CSS is guaranteed, and the old indigo/blue-slate combo
            predated the v3 design system. */}
        <div style={{
          minHeight:"100vh", background:"#000",
          display:"flex", flexDirection:"column",
          alignItems:"center", justifyContent:"center", gap:24
        }}>
          <div style={{textAlign:"center", marginBottom:8}}>
            <div style={{fontSize:36, marginBottom:8, color:"#C89B3C"}}>◎</div>
            <h1 style={{fontSize:30, fontWeight:400, color:"#C89B3C", fontFamily:"'Instrument Serif', Georgia, serif", fontStyle:"italic", marginBottom:4}}>Atlas</h1>
            <p style={{color:"#8A8578", fontSize:13}}>Tracking the AI supply chain</p>
          </div>
          <SignIn routing="hash" />
        </div>
      </SignedOut>
      <SignedIn>
        {isV3Force ? (
          <ErrorBoundary>
            <DesktopV3Router />
          </ErrorBoundary>
        ) : isMobile ? (
          <ErrorBoundary>
            {booting && <BootSequence onDone={endBoot} />}
            <V2Router />
          </ErrorBoundary>
        ) : (
          <ErrorBoundary>
            <DesktopV3Router />
          </ErrorBoundary>
        )}
      </SignedIn>
      <Analytics />
    </>
  );
}
